'use client';

import { CustomImageNotFallback } from '@/components/custom-image-with-fallback';
import { APP_ASSETS } from '@/constant/assets';
import { pageCustomerDirectories } from '@/constant/route';
import useMediaQuery from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';
import { Link } from '@/navigation';
import { screenSize } from '@/styles/styles';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useCustomerSidebarStore } from '../../store';

export const HeaderBrand = () => {
    const trans = useTranslations();

    const {
        isShowSidebar: isOpen,
        setIsShowSidebar: setOpen,
        setIsShowMenuOverlay,
    } = useCustomerSidebarStore();

    const title = trans('BrandInfo.BrandInfo_Cust_AppName').toUpperCase();

    const isDesktop = useMediaQuery(`(min-width: ${screenSize.lg}px)`);

    const handleToggleSidebar = () => {
        if (isDesktop) {
            setOpen(!isOpen);
        } else {
            setIsShowMenuOverlay((st) => !st);
        }
    };

    const content = (
        <>
            <Link
                title={title}
                href={pageCustomerDirectories.ProcedureList}
                className={cn(
                    'ml-4 flex h-full flex-1 items-center justify-center text-primary lg:hidden',
                    {}
                )}
            >
                <div className='mr-2 w-11'>
                    <Image
                        alt={`LOGO ${title}`}
                        width={44}
                        height={44}
                        src={APP_ASSETS.LOGO_COLOR_NO_TEXT}
                        className='h-full w-full min-w-11'
                    />
                </div>
                <div className='space-y-0.5 whitespace-nowrap font-semibold uppercase max-xs:hidden'>
                    <h3 className={cn('text-[11px] text-color-1')}>{title}</h3>

                    <h1 className={cn('text-sm text-primary')}>
                        {trans('BrandInfo.BrandInfo_BrandName')}
                    </h1>
                </div>
            </Link>
            <div
                className={cn('hidden h-full items-center gap-1 lg:flex', {
                    'px-2 max-xl:pl-1': isOpen,
                    'justify-center': !isOpen,
                })}
            >
                {APP_ASSETS.LOGO_WHITE_TEXT.length > 0 && isOpen ? (
                    <>
                        <Link
                            title={title}
                            href={pageCustomerDirectories.ProcedureList}
                            className='mx-1 flex w-60'
                        >
                            <CustomImageNotFallback
                                alt={`LOGO ${trans('BrandInfo.BrandInfo_Cust_AppName')}`}
                                width={40}
                                height={40}
                                src={APP_ASSETS.LOGO_WHITE_TEXT}
                                className='h-full w-full'
                            />
                        </Link>
                    </>
                ) : (
                    <>
                        <Link
                            title={title}
                            href={pageCustomerDirectories.ProcedureList}
                            className='mx-1 flex w-12'
                        >
                            <Image
                                alt={`LOGO ${trans('BrandInfo.BrandInfo_Cust_AppName')}`}
                                width={56}
                                height={56}
                                src={APP_ASSETS.LOGO_NO_TEXT}
                                className='h-full w-full'
                            />
                        </Link>
                        <Link
                            title={title}
                            href={pageCustomerDirectories.ProcedureList}
                            className={cn('mt-0.5', {
                                hidden: !isOpen,
                            })}
                        >
                            <p
                                className={cn(
                                    'text-balance text-[10px] font-semibold text-muted'
                                )}
                            >
                                {title}
                            </p>
                            <p
                                className={cn(
                                    'whitespace-nowrap text-balance text-sm font-semibold uppercase text-primary-foreground 2xl:text-base'
                                )}
                            >
                                {trans(
                                    'BrandInfo.BrandInfo_BrandName'
                                ).toUpperCase()}
                            </p>
                        </Link>
                    </>
                )}
            </div>
        </>
    );

    return (
        <div
            className={cn(
                'relative w-full max-lg:pl-9 lg:bg-primary lg:transition-all',
                {
                    'lg:w-64 lg:min-w-64 2xl:w-80 2xl:min-w-80': isOpen,
                    'lg:w-[72px]': !isOpen,
                }
            )}
        >
            {content}
            <div className='absolute inset-0 left-1 top-0 flex items-center justify-center max-lg:right-full lg:left-full'>
                <button
                    onClick={() => handleToggleSidebar()}
                    className='ml-8 flex h-full items-center justify-center rounded-r-full bg-primary pr-3 text-primary-foreground'
                >
                    <div className='transition-all hover:scale-110'>
                        {isOpen ? (
                            <ChevronLeft size={28} strokeWidth={2.5} />
                        ) : (
                            <ChevronRight size={28} strokeWidth={2.5} />
                        )}
                    </div>
                </button>
            </div>
        </div>
    );
};

HeaderBrand.displayName = 'HeaderBrand';
